/**
 * Space Actions - Canonical Mutation API for Windows
 * 
 * All window operations flow through these actions, which then use
 * the internal _applyLocalWindow* helpers.
 * 
 * All actions handle:
 * - CRDT synchronization
 * - Backend persistence
 * - State invariants (activeWindowId, z-index, etc.)
 */

import { NormalizedWindowEntity } from './spaceStore';
import { createFileLogger } from '../utils/logger';
import { generateWindowId } from '../components/Space/utils/windowUtils';
import { entityActions } from '../stores/entityActions';
import { entityRegistry } from '../stores/entityRegistry';
import { upsertEntity } from '../stores/entityMutations';

const log = createFileLogger('SPACE_ACTIONS');

export const createSpaceActions = (set: any, get: any) => ({
  /**
   * Create a new window
   * @param params - Window creation parameters
   * @returns Window ID
   */
  createWindow: async (params: {
    appType: string;
    entity?: any;
    title?: string;
    position?: { x: number; y: number };
    size?: { width: number; height: number };
    applicationState?: any;
    props?: Record<string, any>;
  }): Promise<string> => {
    // ✅ FIX: Always generate a proper window ID, never reuse entity llmId
    const windowId = generateWindowId();
    
    log.info(`[createWindow] Creating window: windowId=${windowId} appType=${params.appType}`);
    
    // Build the complete window object with all required fields.
    // entity is intentionally NOT embedded — windows hold only entityId references.
    // getWindowEntity() resolves from the entity registry (the single source of truth).
    const newWindow: NormalizedWindowEntity = {
      id: windowId,
      type: 'window',
      appType: params.appType,
      entityId: params.entity?._id || params.entity?.id,
      entityLlmId: params.entity?.llmId,
      position: params.position || { x: 100, y: 100 },
      size: params.size || { width: 800, height: 600 },
      applicationState: params.applicationState || {},
      props: params.props,
      title: params.title || params.entity?.name || `${params.appType.charAt(0).toUpperCase() + params.appType.slice(1)}`,
    };
    
    log.info(`[createWindow] 📝 Built window: windowId=${windowId} appType=${newWindow.appType} entityId=${newWindow.entityId || 'none'} title="${newWindow.title}"`);

    // Register entity in the entity store BEFORE adding the window to the space store.
    // windowContents (in Space.tsx) is a useMemo keyed on windowsStableKey — it calls
    // renderWindowContent() the instant the window is added, which calls getWindowEntity().
    // If the entity isn't in the entity store at that exact moment, getWindowEntity() returns
    // null and the result is cached. Moving registration here ensures entity is available.
    // Entities passed to createWindow are already persisted in the backend (fetched via
    // entityService.get()), so we register locally without triggering a CRDT write.
    if (params.entity?._id) {
      try {
        const alreadyInStore = (entityRegistry.dumpAll().flatMap((__s) => __s.entities) as any[]).some(
          (e: any) => e._id === params.entity!._id
        );
        if (!alreadyInStore) {
          upsertEntity(params.entity, {
            source: 'local',
            syncCRDT: false,
            log: false,
          });
          log.info(`[createWindow] Registered entity in entity store: ${params.entity._id}`);
        }
      } catch { /* non-fatal */ }
    }
    
    // Use the mutation funnel (upsert behavior)
    get()._applyLocalWindowUpdate(windowId, newWindow, {
      source: 'local',
      syncCRDT: true,
      log: true
    });
    
    log.info(`[createWindow] Window added to state: windowId=${windowId} total=${Object.keys(get().windows).length}`);

    // Append the new window to the tab order so it lands at the end of the tab bar.
    // When no order exists yet we seed from the current windows map (lazy migration
    // for existing users who have never dragged a tab). `reorderWindowTabs` is the
    // canonical write-path and handles dedup + stale-id filtering, so we only need
    // to describe the desired sequence here.
    const state = get();
    const existingOrder = state.activeSpace?.settings?.windowTabOrder;
    const baseOrder = (existingOrder && existingOrder.length > 0)
      ? (existingOrder as string[]).filter((id: string) => id !== windowId)
      : Object.keys(state.windows).filter((id: string) => id !== windowId);
    get().reorderWindowTabs([...baseOrder, windowId]);

    // Emit entity.opened for all entity window creations so AgentSupervisor can react.
    // Centralised here so every open path (sidebar, search, chat cards, doc links, etc.)
    // fires the event — fire-and-forget, never fatal.
    if (params.entity?._id) {
      try {
        const { emitEvent, getActiveChatThread } = await import('../services/worldEventsService');
        const spaceId = get().activeSpace?.id || '';
        emitEvent('entity.opened', {
          entityId: params.entity._id,
          entityType: params.entity.entityType,
          name: params.entity.name || '',
          currentThreadId: getActiveChatThread(spaceId),
        }, spaceId);
      } catch { /* non-fatal */ }
    }

    return windowId;
  },

  /**
   * Update an existing window
   * @param windowId - Window ID to update
   * @param updates - Partial window updates
   */
  updateWindow: async (windowId: string, updates: Partial<NormalizedWindowEntity>): Promise<void> => {
    const currentWindow = get().windows[windowId];
    
    if (!currentWindow) {
      log.warn('[updateWindow] Window not found:', windowId);
      return;
    }
    
    log.info('[updateWindow] Updating window:', { windowId, updates: Object.keys(updates) });

    // Clean up transient launcher entities (Window, OpenDialog) when navigating away.
    // These are ephemeral entities created by the NewWindow/OpenDialog launcher that must be
    // deleted when the window navigates to real content.
    const oldEntityId = currentWindow.entityId;
    const isNavigatingAway = updates.entityId !== undefined && updates.entityId !== oldEntityId;
    const isSwitchingApp = updates.appType !== undefined && updates.appType !== currentWindow.appType;

    if ((isNavigatingAway || isSwitchingApp) && oldEntityId) {
      const oldEntity = (entityRegistry.dumpAll().flatMap((__s) => __s.entities) as any[]).find(
        (e: any) => e._id === oldEntityId || e.id === oldEntityId
      );
      const isTransientEntity = oldEntity && (
        oldEntity.entityType === 'Window' ||
        oldEntity.entityType === 'OpenDialog'
      );
      if (isTransientEntity) {
        log.info('[updateWindow] Deleting transient launcher entity:', { oldEntityId, entityType: oldEntity.entityType });
        try {
          await entityActions.softDeleteEntity(oldEntityId);
          log.info('[updateWindow] Transient launcher entity deleted:', oldEntity.entityType);
        } catch (error) {
          log.error('[updateWindow] Failed to delete transient entity (continuing):', error);
        }
      }
    }
    
    // Use helper to update window (handles CRDT sync and state update)
    get()._applyLocalWindowUpdate(windowId, updates, {
      source: 'local',
      syncCRDT: true,
      log: true
    });
  },

  /**
   * Close a window
   * @param windowId - Window ID to close
   */
  closeWindow: async (windowId: string): Promise<void> => {
    const currentWindow = get().windows[windowId];
    
    if (!currentWindow) {
      log.warn('[closeWindow] Window not found:', windowId);
      return;
    }
    
    log.info('[closeWindow] Closing window:', { windowId });
    
    // If window has a Window/OpenDialog entity backing it, soft delete that entity too
    // This includes:
    // 1. entityId - Direct Window/OpenDialog entity (NewWindow, OpenDialog)
    // 2. windowEntityId in applicationState - Tracking entity for regular apps
    if (currentWindow.entityId) {
      const entity = (entityRegistry.dumpAll().flatMap((__s) => __s.entities) as any[]).find(
        (e: any) => e._id === currentWindow.entityId
      );
      
      const isWindowEntity = entity && (entity.entityType === 'Window' || entity.entityType === 'OpenDialog');
      if (isWindowEntity) {
        log.info('[closeWindow] Deleting window entity:', {
          entityId: entity._id,
          entityType: entity.entityType,
          entityName: entity.name
        });
        
        try {
          // Use entityActions for proper orchestration (REST + registry + events).
          await entityActions.softDeleteEntity(entity._id);
          log.info('[closeWindow] ✅ Window entity deleted');
        } catch (error) {
          log.warn('[closeWindow] Failed to delete window entity (continuing with window close):', error);
        }
      }
    }
    
    // Also check for windowEntityId in applicationState (for app windows with tracking entities)
    const windowEntityId = currentWindow.applicationState?.windowEntityId;
    if (windowEntityId) {
      log.info('[closeWindow] Deleting tracking Window entity from applicationState:', {
        windowEntityId,
        appType: currentWindow.appType
      });
      
      try {
        await entityActions.softDeleteEntity(windowEntityId);
        log.info('[closeWindow] ✅ Tracking Window entity deleted');
      } catch (error) {
        log.warn('[closeWindow] Failed to delete tracking Window entity:', error);
      }
    }
    
    // ✅ Use removeWindow instead of _applyLocalWindowDelete directly
    // This ensures focus management and child window cleanup work properly
    get().removeWindow(windowId);
  },

  /**
   * Set active window.
   * Delegates to the top-level setActiveWindow on the store which handles
   * localStorage persistence, backend save scheduling, and event dispatch.
   */
  setActiveWindow: (windowId: string | null): void => {
    log.info('[setActiveWindow] Setting active window:', windowId);
    get().setActiveWindow(windowId);
  },

  /**
   * Navigate window to a new entity (adds to navigation history)
   * @param windowId - Window ID to navigate
   * @param entityId - Entity ID to navigate to
   */
  navigateWindow: async (windowId: string, entityId: string): Promise<void> => {
    const currentWindow = get().windows[windowId];
    
    if (!currentWindow) {
      log.warn('[navigateWindow] Window not found:', windowId);
      return;
    }
    
    log.info(`[navigateWindow] Navigating window: windowId=${windowId} entityId=${entityId}`);
    
    // Clean up old transient (NewWindow/OpenDialog) entity if this window was showing one
    const oldEntityId = currentWindow.entityId;
    if (oldEntityId) {
      const oldEntity = (entityRegistry.dumpAll().flatMap((__s) => __s.entities) as any[]).find(
        (e: any) => e._id === oldEntityId || e.id === oldEntityId
      );
      const isTransientEntity = oldEntity && (
        oldEntity.entityType === 'Window' ||
        oldEntity.entityType === 'OpenDialog'
      );
      if (isTransientEntity) {
        log.info('[navigateWindow] Deleting transient launcher entity:', oldEntityId, oldEntity.entityType);
        try {
          await entityActions.softDeleteEntity(oldEntityId);
        } catch (error) {
          log.error('[navigateWindow] Failed to delete transient entity:', error);
        }
      }
    }
    
    // Get current history and index
    const history = currentWindow.navigationHistory || [];
    const currentIndex = currentWindow.navigationIndex ?? -1;
    
    // If we're not at the end of history, truncate forward history
    const newHistory = currentIndex >= 0 ? history.slice(0, currentIndex + 1) : [];
    
    // Add new entity to history
    newHistory.push(entityId);
    
    // Get the entity to navigate to
    const entity = (entityRegistry.dumpAll().flatMap((__s) => __s.entities) as any[]).find(
      (e: any) => e._id === entityId || e.id === entityId || e.llmId === entityId
    );
    
    if (!entity) {
      log.warn('[navigateWindow] Entity not found:', entityId);
      return;
    }
    
    const shouldReplaceLauncherApp =
      currentWindow.appType === 'new-window' ||
      (currentWindow.applicationState as any)?.isNew === true;
    let nextAppType: string | undefined;
    if (shouldReplaceLauncherApp) {
      const { getAppTypeForEntity } = await import('../utils/entityTypeMapping');
      nextAppType = getAppTypeForEntity(entity);
    }

    // Update window with new entity and navigation state. Most app windows keep their
    // appType while navigating internally; transient launchers replace themselves with
    // the target entity's app.
    get()._applyLocalWindowUpdate(windowId, {
      ...(nextAppType ? { appType: nextAppType } : {}),
      entityId: entity._id || entity.id,
      entityLlmId: entity.llmId,
      title: entity.name,
      navigationHistory: newHistory,
      navigationIndex: newHistory.length - 1,
      applicationState: {
        ...currentWindow.applicationState,
        isNew: false
      }
    }, {
      source: 'local',
      syncCRDT: true,
      log: true
    });

    log.info(`[navigateWindow] Navigation complete: windowId=${windowId} entityId=${entityId} historyLen=${newHistory.length}`);
  },

  /**
   * Clear the entityId (and optionally reset the title) from a window.
   * Called when an app deselects its current entity (e.g. navigating back to inbox).
   * Syncs to CRDT/backend so a refresh doesn't restore the stale reference.
   */
  clearWindowEntityId: (windowId: string, fallbackTitle?: string): void => {
    const currentWindow = get().windows[windowId];
    if (!currentWindow) return;

    // Skip if there is nothing to clear and the title already matches — true no-op.
    const titleAlreadyCurrent = !fallbackTitle || currentWindow.title === fallbackTitle;
    if (!currentWindow.entityId && titleAlreadyCurrent) return;

    log.info(`[clearWindowEntityId] Clearing entity from window: windowId=${windowId} clearedId=${currentWindow.entityId} fallbackTitle="${fallbackTitle || ''}"`);
    const updates: Partial<import('./spaceStore').NormalizedWindowEntity> = {
      entityId: undefined,
      entityLlmId: undefined,
    };
    if (fallbackTitle !== undefined) {
      updates.title = fallbackTitle;
    }
    get()._applyLocalWindowUpdate(windowId, updates, {
      source: 'local',
      syncCRDT: true,
      log: false,
    });
  },

  /**
   * Navigate back in window history
   * @param windowId - Window ID to navigate
   */
  navigateBack: async (windowId: string): Promise<void> => {
    const currentWindow = get().windows[windowId];
    
    if (!currentWindow || !currentWindow.navigationHistory || currentWindow.navigationHistory.length === 0) {
      log.warn('[navigateBack] No history to navigate back');
      return;
    }
    
    const currentIndex = currentWindow.navigationIndex ?? currentWindow.navigationHistory.length - 1;
    
    if (currentIndex <= 0) {
      log.warn('[navigateBack] Already at beginning of history');
      return;
    }
    
    const newIndex = currentIndex - 1;
    const entityId = currentWindow.navigationHistory[newIndex];
    
    log.info('[navigateBack] Navigating back:', { windowId, fromIndex: currentIndex, toIndex: newIndex, entityId });
    
    // Get the entity
    const entity = (entityRegistry.dumpAll().flatMap((__s) => __s.entities) as any[]).find(
      (e: any) => e._id === entityId || e.id === entityId || e.llmId === entityId
    );
    
    if (!entity) {
      log.warn('[navigateBack] Entity not found in history:', entityId);
      return;
    }
    
    const { getAppTypeForEntity } = await import('../utils/entityTypeMapping');
    const appType = getAppTypeForEntity(entity);
    
    // Update window to show previous entity
    get()._applyLocalWindowUpdate(windowId, {
      entityId: entity._id || entity.id,
      entityLlmId: entity.llmId,
      appType,
      title: entity.name,
      navigationIndex: newIndex
    }, {
      source: 'local',
      syncCRDT: true,
      log: true
    });
  },

  /**
   * Navigate forward in window history
   * @param windowId - Window ID to navigate
   */
  navigateForward: async (windowId: string): Promise<void> => {
    const currentWindow = get().windows[windowId];
    
    if (!currentWindow || !currentWindow.navigationHistory || currentWindow.navigationHistory.length === 0) {
      log.warn('[navigateForward] No history to navigate forward');
      return;
    }
    
    const currentIndex = currentWindow.navigationIndex ?? currentWindow.navigationHistory.length - 1;
    
    if (currentIndex >= currentWindow.navigationHistory.length - 1) {
      log.warn('[navigateForward] Already at end of history');
      return;
    }
    
    const newIndex = currentIndex + 1;
    const entityId = currentWindow.navigationHistory[newIndex];
    
    log.info('[navigateForward] Navigating forward:', { windowId, fromIndex: currentIndex, toIndex: newIndex, entityId });
    
    // Get the entity
    const entity = (entityRegistry.dumpAll().flatMap((__s) => __s.entities) as any[]).find(
      (e: any) => e._id === entityId || e.id === entityId || e.llmId === entityId
    );
    
    if (!entity) {
      log.warn('[navigateForward] Entity not found in history:', entityId);
      return;
    }
    
    const { getAppTypeForEntity } = await import('../utils/entityTypeMapping');
    const appType = getAppTypeForEntity(entity);
    
    // Update window to show next entity
    get()._applyLocalWindowUpdate(windowId, {
      entityId: entity._id || entity.id,
      entityLlmId: entity.llmId,
      appType,
      title: entity.name,
      navigationIndex: newIndex
    }, {
      source: 'local',
      syncCRDT: true,
      log: true
    });
  },
});
